import {ChangeDetectionStrategy, Component, computed, inject, input, signal} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatMenuModule} from '@angular/material/menu';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {ChallengeBuilderProject, Project} from 'src/app/core/model/project';
import {
  GetChallengeBuilderProjectAction,
  StopChallengeAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {
  DeleteProjectAction,
  DuplicateProjectAction,
  GetProjectsAction,
} from 'src/app/core/store/projects/projects.action';
import {GetAllTrackersAction} from 'src/app/core/store/trackers/trackers.action';
import {MessageService} from 'src/app/services/message.service';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {ChallengeWarningDialogComponent} from 'src/app/shared/projects/challenges/challenge-warning-dialog/challenge-warning-dialog.component';
import {CbLaunchChallengeDialogComponent} from '../../challenge-builder/shared/cb-launch-challenge-dialog/cb-launch-challenge-dialog.component';
import {
  CHALLENGE_ROW_MENU_OPTIONS,
  PROJECT_ROW_MENU_OPTIONS,
  PROJECTS_DIALOG_CONFIG,
  ProjectRowMenuOption,
} from '../../shared/projects-constants';
import {ProjectsDialogComponent} from '../../dialogs/projects-dialog/projects-dialog.component';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'project-row-menu',
  standalone: true,
  imports: [MatMenuModule, OphLoadingModule],
  templateUrl: './project-row-menu.component.html',
  styleUrl: './project-row-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectRowMenuComponent {
  private readonly mobileService = inject(MobileService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private messageService = inject(MessageService);

  project = input.required<Project>();
  inHeader = input<boolean>(false);
  isChallenge = input<boolean>(false);
  loading = signal(false);

  menuOptions = computed<ProjectRowMenuOption[]>(() => {
    if (!this.isChallenge()) return PROJECT_ROW_MENU_OPTIONS;
    if (this.project().status !== 'active') {
      return CHALLENGE_ROW_MENU_OPTIONS.filter(o => o.action !== 'startOver' && o.action !== 'stop');
    }
    return CHALLENGE_ROW_MENU_OPTIONS.filter(o => o.action !== 'delete');
  });

  onAction(option: ProjectRowMenuOption): void {
    const action = option.action;
    if (action === 'edit') {
      this.onEdit();
      return;
    }
    if (action === 'duplicate') {
      this.onDuplicate();
      return;
    }
    if (action === 'delete') {
      this.onDelete();
      return;
    }
    if (action === 'startOver') {
      this.onStartOver();
      return;
    }
    if (action === 'stop') {
      this.onStop();
      return;
    }
    if (action === 'share') {
      this.onShare();
      return;
    }
  }

  onEdit(): void {
    const project = this.project();
    if (!project?._id) return;
    if (project.isChallenge) {
      this.router.navigate(['/projects', 'challenge-builder', project._id, 'overview']);
      return;
    }
    const dialogRef = this.dialog.open(ProjectsDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      ...PROJECTS_DIALOG_CONFIG,
      data: {project},
    });
    dialogRef.afterClosed().subscribe(updated => {
      if (updated) {
        this.onActionSuccess(updated);
      }
    });
  }

  onStartOver(): void {
    const projectId = this.project()?._id;
    if (!projectId) return;
    this.dialog.open(ChallengeWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      backdropClass: 'oph-backdrop',
      panelClass: 'challenge-warning-dialog-panel',
      data: {
        type: 'challenge',
        action: 'startOver',
        onConfirm: ({onSuccess, onFailure}) => {
          this.store.dispatch(
            new StopChallengeAction({
              projectId,
              onSuccess: (cbProject: ChallengeBuilderProject) => {
                this.store.dispatch(new GetChallengeBuilderProjectAction({projectId}));
                this.store.dispatch(new GetProjectsAction({}));
                onSuccess();
                this.dialog.open(CbLaunchChallengeDialogComponent, {
                  autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
                  backdropClass: 'oph-backdrop',
                  panelClass: 'cb-launch-challenge-dialog-panel',
                  width: '100%',
                  maxWidth: '700px',
                  data: {cbProject},
                });
              },
              onFailure: () => onFailure(new Error('Error stopping challenge')),
            })
          );
        },
      },
    });
  }

  onStop(): void {
    const projectId = this.project()?._id;
    if (!projectId) return;
    this.dialog.open(ChallengeWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      backdropClass: 'oph-backdrop',
      panelClass: 'challenge-warning-dialog-panel',
      data: {
        type: 'challenge',
        action: 'stop',
        onConfirm: ({onSuccess, onFailure}) => {
          this.store.dispatch(
            new StopChallengeAction({
              projectId,
              onSuccess: () => {
                this.store.dispatch(new GetChallengeBuilderProjectAction({projectId}));
                this.store.dispatch(new GetProjectsAction({}));
                onSuccess();
              },
              onFailure: () => onFailure(new Error('Error stopping challenge')),
            })
          );
        },
      },
    });
  }

  onShare(): void {
    console.log('Share', this.project());
  }

  onDuplicate(): void {
    const project = this.project();
    if (!project._id) return;
    this.loading.set(true);
    this.store.dispatch(
      new DuplicateProjectAction({
        id: project._id,
        onSuccess: duplicatedProject => {
          this.loading.set(false);
          this.onActionSuccess(duplicatedProject);
        },
        onFailure: err => {
          this.loading.set(false);
          this.onActionFailure(err);
        },
      })
    );
  }

  onDelete(): void {
    const project = this.project();
    if (!project._id) return;
    this.dialog.open(ChallengeWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      backdropClass: 'oph-backdrop',
      panelClass: 'challenge-warning-dialog-panel',
      data: {
        type: project.isChallenge ? 'challenge' : 'project',
        action: 'delete',
        onConfirm: ({onSuccess, onFailure}) => {
          this.store.dispatch(
            new DeleteProjectAction({
              id: project._id,
              onSuccess: () => {
                this.store.dispatch(new GetProjectsAction({}));
                onSuccess();
              },
              onFailure: err => onFailure(err),
            })
          );
        },
      },
    });
  }

  private onActionFailure(err: Error): void {
    this.messageService.add(err.message || 'There was an error with the Project.');
  }

  private onActionSuccess(project: Project): void {
    this.store.dispatch(new GetProjectsAction({}));
    this.store.dispatch(new GetAllTrackersAction({params: {projectId: project._id, pageSize: 1000}}));
    if (project._id) {
      sessionStorage.setItem('ophStoredProjectId', project._id);
    }
  }
}
